import { glsl_functions, GLSLSource } from "./glsl";
import { Buffer, bufferType, GL_BYTE, GL_FLOAT, GL_INT, GL_SHORT, GL_UNSIGNED_BYTE, GL_UNSIGNED_INT, GL_UNSIGNED_SHORT, glTypeToTypedArray, } from "./shader/buffer";
import { gridSize } from "./utils";
const srcFunction = glsl_functions.find(f => f.name === "src");
const GL_ALPHA = 0x1906;
const GL_RGB = 0x1907;
const GL_RGBA = 0x1908;
const GL_LUMINANCE = 0x1909;
const GL_LUMINANCE_ALPHA = 0x190a;
const GL_RED = 0x1903;
const GL_RG = 0x8227;
const GL_RED_INTEGER = 0x8d94;
const GL_RG_INTEGER = 0x8228;
const GL_RGB_INTEGER = 0x8d98;
const GL_RGBA_INTEGER = 0x8d99;
const GL_R8 = 0x8229;
const GL_RGBA8 = 0x8058;
const GL_RGBA32F = 0x8814;
const GL_RGBA16F = 0x881a;
const GL_RGBA16I = 0x8d88;
const GL_RGBA16UI = 0x8d76;
const GL_RGBA32I = 0x8d82;
const GL_RGBA32UI = 0x8d70;
const GL_RGBA8I = 0x8d8e;
export const TEXTURE_FORMATS = {
    rgba: GL_RGBA,
    rgb: GL_RGB,
    luminance: GL_LUMINANCE,
    luminance_alpha: GL_LUMINANCE_ALPHA,
    alpha: GL_ALPHA,
    red: GL_RED,
    rg: GL_RG,
};
export const TEXTURE_INTERNAL_FORMATS = {
    rgba8: GL_RGBA8,
    rgba32f: GL_RGBA32F,
    rgba16f: GL_RGBA16F,
    rgba16i: GL_RGBA16I,
    rgba16ui: GL_RGBA16UI,
    rgba32i: GL_RGBA32I,
    rgba32ui: GL_RGBA32UI,
    rgba8i: GL_RGBA8I,
    r8: GL_R8,
};
const INTERNAL_FORMAT_DEFAULTS = {
    rgba8: { type: GL_UNSIGNED_BYTE, format: GL_RGBA },
    rgba32f: { type: GL_FLOAT, format: GL_RGBA },
    rgba16f: { type: GL_FLOAT, format: GL_RGBA },
    rgba16i: { type: GL_SHORT, format: GL_RGBA_INTEGER },
    rgba16ui: { type: GL_UNSIGNED_SHORT, format: GL_RGBA_INTEGER },
    rgba32i: { type: GL_INT, format: GL_RGBA_INTEGER },
    rgba32ui: { type: GL_UNSIGNED_INT, format: GL_RGBA_INTEGER },
    rgba8i: { type: GL_BYTE, format: GL_RGBA_INTEGER },
    r8: { type: GL_UNSIGNED_BYTE, format: GL_RED },
};
function textureFormat(value) {
    if (!value)
        return undefined;
    if (typeof value === "number")
        return value;
    const key = value ? value.toLowerCase() : undefined;
    if (!(key in TEXTURE_FORMATS)) {
        console.warn(`Unknown texture format "${key}"`);
        return undefined;
    }
    return TEXTURE_FORMATS[key];
}
function textureInternalFormat(value) {
    if (!value)
        return undefined;
    if (typeof value === "number")
        return value;
    const key = value ? value.toLowerCase() : undefined;
    if (!(key in TEXTURE_INTERNAL_FORMATS)) {
        console.warn(`Unknown texture internal format "${key}"`);
        return undefined;
    }
    return TEXTURE_INTERNAL_FORMATS[key];
}
let t = 1;
export class Texture {
    constructor(synth, props = {}, add = true) {
        if (props instanceof Buffer) {
            const [width, height] = gridSize(props._size);
            props = {
                data: props,
                type: props._type,
                internalFormat: props._type === GL_FLOAT ? synth.renderer.gl.RGBA32F : synth.renderer.gl.RGBA8,
                format: synth.renderer.gl.RGBA,
                width,
                height,
            };
        }
        this.name = props.name || "texture_" + t++;
        this.synth = synth;
        this.width = props.width || synth.width;
        this.height = props.height || synth.height;
        this.ratio = this.width / this.height;
        const internalFormat = textureInternalFormat(props.internalFormat);
        const format = textureFormat(props.format);
        this._internalFormat = internalFormat ?? synth.renderer.textureInternalFormat;
        this._format = format ?? synth.renderer.textureFormat;
        this.type((props.type || synth.renderer.textureType));
        this.len = props.len || 1;
        this.index = 0;
        this.textures = new Array(this.len);
        this.framebuffers = new Array(this.len);
        this.needsUpdate = true;
        this.updates = 0;
        this._clamp = props.clamp;
        this._filter = props.filter;
        this._renderbuffer = props.renderbuffer;
        this.create(props.data, {
            clamp: this._clamp,
            filter: this._filter,
        });
        if (add)
            this.synth.textures.push(this);
    }
    type(type) {
        const resolved = typeof type === "number" ? type : bufferType(type.toLowerCase());
        if (typeof resolved !== "number") {
            console.warn(`Unknown buffer type "${type}"`);
            return this;
        }
        this._type = resolved;
        this._internalFormat = this._type === GL_FLOAT ? this.synth.renderer.gl.RGBA32F : this.synth.renderer.gl.RGBA8;
        this._format = this.synth.renderer.gl.RGBA;
        return this.create(this.source, {
            clamp: this._clamp,
            filter: this._filter,
        });
    }
    format(format, internalFormat) {
        const resolvedFormat = textureFormat(format);
        if (!resolvedFormat)
            return this;
        this._format = resolvedFormat;
        if (internalFormat) {
            const resolvedInternalFormat = textureInternalFormat(internalFormat);
            if (resolvedInternalFormat)
                return this.internalFormat(resolvedInternalFormat);
        }
        this.create(this.source, {
            clamp: this._clamp,
            filter: this._filter,
        });
        return this;
    }
    internalFormat(internalFormat) {
        const resolved = textureInternalFormat(internalFormat);
        if (!resolved)
            return this;
        this._internalFormat = resolved;
        const defaults = INTERNAL_FORMAT_DEFAULTS[internalFormat];
        defaults && (this._type = defaults.type);
        defaults && (this._format = defaults.format);
        if (this.isIntegerFormat())
            this._filter = "nearest";
        this.create(this.source, {
            clamp: this._clamp,
            filter: this._filter,
        });
        this.shader?.forceUpdate();
        return this;
    }
    filter(filter) {
        if (filter === "linear" && this.isIntegerFormat()) {
            console.warn(`Texture "${this.name}" uses an integer format; "linear" filtering is not supported.`);
            return this;
        }
        this._filter = filter;
        return this.create(this.source, {
            clamp: this._clamp,
            filter: this._filter,
        });
    }
    realloc(len) {
        this.len = len;
        this.textures = new Array(this.len);
        this.framebuffers = new Array(this.len);
        return this.create(this.source, {
            clamp: this._clamp,
            filter: this._filter,
        });
    }
    create(source = null, textureParams) {
        const gl = this.synth.renderer.gl;
        if (Array.isArray(source) || ArrayBuffer.isView(source)) {
            source = new Buffer(this.synth, { data: source, name: "buffer_" + this.name });
            if (this._type !== source._type)
                this.type(source._type);
        }
        if (source && "width" in source && source.width && "height" in source && source.height) {
            this.width = source.width;
            this.height = source.height;
        }
        else if (source && "videoWidth" in source && source.videoWidth && "videoHeight" in source && source.videoHeight) {
            this.width = source.videoWidth;
            this.height = source.videoHeight;
        }
        this.ratio = this.width / this.height;
        for (let i = 0; i < this.len; i++) {
            if (this.textures[i]) {
                gl.deleteTexture(this.textures[i]);
                this.textures[i] = null;
            }
            if (this.framebuffers[i]) {
                gl.deleteFramebuffer(this.framebuffers[i]);
                this.framebuffers[i] = null;
            }
        }
        for (let i = 0; i < this.len; i++) {
            const [framebuffer, texture] = this.synth.renderer.createFramebufferAndTexture(source, this.width, this.height, this._internalFormat, this._type, this._format, this._renderbuffer, textureParams);
            texture["name"] = this.len > 1 ? this.name + "_" + i : this.name;
            this.textures[i] = texture;
            this.framebuffers[i] = framebuffer;
        }
        this.source = source;
        //this.pixelsData = new (glTypeToTypedArray(this._type))(this.width * this.height * this.components())
        this.pixelsData = new (glTypeToTypedArray(this._type))(this.width * this.height * 4);
        // this.needsUpdate = source !== null
        this.updates = 0;
        return this;
    }
    activate(active = true) {
        this.needsUpdate = active;
    }
    components() {
        const gl = this.synth.renderer.gl;
        switch (this._format) {
            case gl.ALPHA:
            case gl.LUMINANCE:
            case gl.RED:
                return 1;
            case gl.LUMINANCE_ALPHA:
            case gl.RG:
                return 2;
            case gl.RGB:
                return 3;
            case gl.RGBA:
                return 4;
            default:
                throw new Error("Formato di texture sconosciuto: " + this._format);
        }
    }
    isIntegerFormat() {
        return (this._format === GL_RGBA_INTEGER ||
            this._format === GL_RGB_INTEGER ||
            this._format === GL_RG_INTEGER ||
            this._format === GL_RED_INTEGER);
    }
    isUnsignedType() {
        return this._type === GL_UNSIGNED_BYTE || this._type === GL_UNSIGNED_SHORT || this._type === GL_UNSIGNED_INT;
    }
    samplerType() {
        return this.isIntegerFormat() ? (this.isUnsignedType() ? "usampler2D" : "isampler2D") : "sampler2D";
    }
    integerScale() {
        switch (this._type) {
            case GL_UNSIGNED_BYTE:
                return 255;
            case GL_UNSIGNED_SHORT:
                return 65535;
            case GL_UNSIGNED_INT:
                return 4294967295;
            case GL_BYTE:
                return 127;
            case GL_SHORT:
                return 32767;
            case GL_INT:
                return 2147483647;
            default:
                return 1;
        }
    }
    forceUpdate() {
        const wasActive = this.needsUpdate;
        this.needsUpdate = true;
        this.update();
        this.needsUpdate = wasActive;
        this.shader?.forceUpdate();
    }
    update() {
        if (!this.source || !this.needsUpdate)
            return this;
        const gl = this.synth.renderer.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.texture());
        if (this.source instanceof Buffer) {
            gl.texImage2D(gl.TEXTURE_2D, 0, this._internalFormat, this.width, this.height, 0, this._format, this._type, this.source._data);
        }
        else {
            gl.texImage2D(gl.TEXTURE_2D, 0, this._internalFormat, this._format, this._type, this.source);
        }
        //gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, this.width, this.height, this._format, this._type, this.source)
        this.updates++;
        return this;
    }
    putData(data, x, y, width, height) {
        const gl = this.synth.renderer.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.texture());
        const level = 0;
        const xoffset = x;
        const yoffset = this.height - 1 - y;
        const format = this._format;
        const type = this._type;
        gl.texSubImage2D(gl.TEXTURE_2D, level, xoffset, yoffset, width, height, format, type, data);
        gl.bindTexture(gl.TEXTURE_2D, null);
    }
    // forceUpdate() {
    // 	if (this.attachedShader) this.attachedShader.forceUpdate()
    // }
    texture(index) {
        if (typeof index !== "undefined")
            this.index = (index + this.len) % this.len;
        return this.textures[this.index];
    }
    framebuffer(index) {
        if (typeof index !== "undefined")
            this.index = (index + this.len) % this.len;
        return this.framebuffers[this.index];
    }
    next() {
        this.index = (this.index + 1) % this.len;
        return this;
    }
    length(len) {
        this.len = len;
        return this.create(this.source, {
            clamp: this._clamp,
            filter: this._filter,
        });
    }
    pixels(data = this.pixelsData, flipY = true) {
        const gl = this.synth.renderer.gl;
        const width = this.width;
        const height = this.height;
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer());
        gl.readPixels(0, 0, width, height, this._format, this._type, data);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        // Flip vertically
        if (flipY) {
            const row = width * 4;
            //const row = width * this.components()
            const halfHeight = Math.floor(height / 2);
            for (let i = 0; i < halfHeight; i++) {
                const start = i * row;
                const end = (height - i - 1) * row;
                const temp = data.slice(start, start + row);
                data.copyWithin(start, end, end + row);
                data.set(temp, end);
            }
        }
        return data;
    }
    resize(width, height) {
        if (width === this.width && height === this.height)
            return this;
        this.width = width;
        this.height = height;
        this.ratio = this.width / this.height;
        if (this.source &&
            !(this.source instanceof Buffer ||
                this.source instanceof ImageBitmap ||
                this.source instanceof ImageData ||
                this.source instanceof VideoFrame)) {
            this.source.width = width;
            this.source.height = height;
        }
        this.create(this.source, {
            clamp: this._clamp,
            filter: this._filter,
        });
        this.shader?.resize(width, height);
        return this;
    }
    toGLSLSource() {
        return new GLSLSource(this.synth, srcFunction, [this]);
    }
    init(source) {
        if (this.source) {
            // cleanup old source buffer
            if (this.source instanceof Buffer) {
                this.source.clear();
                // remove from synth buffers
                const index = this.synth.buffers.indexOf(this.source);
                if (index > -1)
                    this.synth.buffers.splice(index, 1);
            }
            else if ("close" in this.source && typeof this.source.close === "function") {
                this.source.close();
                this.source = null;
            }
        }
        if (source instanceof Texture) {
            source.out(this, 0, 0, source.width, source.height, 0, 0, this.width, this.height);
            return this;
        }
        // if source is ArrayBufferView create a buffer
        if (ArrayBuffer.isView(source) || Array.isArray(source)) {
            source = new Buffer(this.synth, { data: source, name: "buffer_" + this.name });
            if (this._type !== source._type)
                this.type(source._type);
        }
        this.source = source;
        this.forceUpdate();
        const gl = this.synth.renderer.gl;
        gl.bindTexture(gl.TEXTURE_2D, null);
        return this;
    }
    out(texture, ...args) {
        const [sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight] = args.length === 0
            ? [0, 0, this.width, this.height, 0, 0, texture.width, texture.height]
            : args.length === 4
                ? [...args, ...args]
                : args;
        const gl = this.synth.renderer.gl;
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, this.framebuffer());
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, texture.framebuffer(texture.index + 1));
        gl.blitFramebuffer(sx, sy, sx + sWidth, sy + sHeight, dx, dy, dx + dWidth, dy + dHeight, gl.COLOR_BUFFER_BIT, texture._filter === "linear" ? gl.LINEAR : gl.NEAREST);
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
        texture.updates++;
        return this;
    }
    toCanvas(canvasOrCtx) {
        const data = this.pixels();
        if (canvasOrCtx instanceof HTMLCanvasElement || canvasOrCtx instanceof OffscreenCanvas) {
            canvasOrCtx.width = this.width;
            canvasOrCtx.height = this.height;
            canvasOrCtx = canvasOrCtx.getContext("2d");
        }
        const imageData = canvasOrCtx.createImageData(this.width, this.height);
        imageData.data.set(data);
        canvasOrCtx.putImageData(imageData, 0, 0);
    }
    toImage(canvas) {
        const data = this.pixels();
        const ctx = canvas.getContext("2d");
        canvas.width = this.width;
        canvas.height = this.height;
        const imageData = ctx.createImageData(this.width, this.height);
        imageData.data.set(data);
        ctx.putImageData(imageData, 0, 0);
        const img = new Image();
        img.src = canvas.toDataURL("image/png");
        return img;
    }
    clear() {
        const gl = this.synth.renderer.gl;
        for (let i = 0; i < this.len; i++) {
            if (this.textures[i]) {
                gl.deleteTexture(this.textures[i]);
                this.textures[i] = null;
            }
            if (this.framebuffers[i]) {
                gl.deleteFramebuffer(this.framebuffers[i]);
                this.framebuffers[i] = null;
            }
        }
        // clear source buffer
        if (this.source && this.source instanceof Buffer) {
            this.source.clear();
            // remove from synth buffers
            const index = this.synth.buffers.indexOf(this.source);
            if (index > -1)
                this.synth.buffers.splice(index, 1);
        }
        this.shader?.clear();
        this.shader = null;
    }
}
